/**
 * Topbar Navigation Component
 * Role-based navigation menu for authenticated users
 * 
 * UI Design extracted from:
 * - prototypes/figma-bquest-app/src/components/FADashboard.tsx (lines 34-57)
 * - prototypes/figma-bquest-app/src/components/ProviderPortal.tsx (lines 59-83)
 * - prototypes/figma-bquest-app/src/components/CareSeeker/Dashboard.tsx (lines 129-152)
 * 
 * Design Patterns:
 * - lg+: fixed left sidebar (see AppSidebar.tsx)
 * - Below lg: sticky green topbar with logo, user menu and hamburger
 *   (MobileMenu sheet holds the navigation items)
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, ChevronDown, LogOut, User as UserIcon, Users } from 'lucide-react';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useAdminAuth } from '@/features/admin-portal/hooks/useAdminAuth';
import { useWorkspace } from '@/features/workspace-sub-delegation';
import { useUserProfile } from '@/features/profile/hooks/useProfile';
import { Button } from '@/shared/components/ui/button';
import bquestLogo from '@/assets/bquest-logo.png';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { MobileMenu } from './MobileMenu';
import { AppSidebar } from './AppSidebar';
import { getUniqueNavigationItemsForRole, getRoleDisplayName, type UserRole } from '../config/navigationItems';

export const Topbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { isAdmin } = useAdminAuth();
  const { effectiveRole } = useWorkspace();
  // Guests render this Topbar too (Care Navigator is public). Skip the
  // authenticated profile fetch for them — otherwise /users/profile/me 401s and
  // the global interceptor bounces to /login.
  const { data: profile } = useUserProfile({ enabled: isAuthenticated });

  // Prefer DB profile name (stays fresh after Account Settings save) over
  // the Kinde JWT name (only refreshed on new login).
  const displayFirstName = profile?.firstName ?? user?.firstName ?? 'User';
  const displayLastName = profile?.lastName ?? user?.lastName;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Feature 075: a SUB_DELEGATE navigates with the EFFECTIVE role (the
  // active workspace owner's role, from WorkspaceProvider). Until a
  // workspace is resolved the nav stays empty. Non-delegates derive
  // directly from their own role — byte-identical to pre-075 behavior and
  // independent of the workspace context.
  const navRole: UserRole | null =
    user && user.role !== 'SUB_DELEGATE'
      ? (user.role as UserRole)
      : effectiveRole && effectiveRole !== 'SUB_DELEGATE'
        ? (effectiveRole as UserRole)
        : null;

  const navItems = user && navRole ? getUniqueNavigationItemsForRole(navRole) : [];
  const roleDisplayName = user
    ? user.role === 'SUB_DELEGATE'
      ? `Sub-delegate${navRole ? ` · ${getRoleDisplayName(navRole)}` : ''}`
      : getRoleDisplayName(user.role as UserRole)
    : '';

  /**
   * Get dashboard route based on user role
   * Feature 008: Navigation Topbar Update - Logo redirects to role-specific dashboard
   */
  const getDashboardRoute = (role: UserRole): string => {
    const dashboardRoutes: Record<UserRole, string> = {
      CARE_SEEKER: '/dashboard/care-seeker',
      CARE_RECIPIENT: '/dashboard/care-seeker',
      SERVICE_PROVIDER: '/dashboard/service-provider',
      FINANCIAL_ADVISOR: '/dashboard/financial-advisor',
      ADMIN: '/admin',
    };
    return dashboardRoutes[role] || '/dashboard';
  };

  // Get user initials for avatar fallback
  const getUserInitials = (firstName: string, lastName?: string) => {
    if (lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    return firstName.slice(0, 2).toUpperCase();
  };

  // Get full name from firstName and lastName
  const getFullName = (firstName: string, lastName?: string) => {
    return [firstName, lastName].filter(Boolean).join(' ');
  };

  const homeRoute = isAuthenticated && user && navRole ? getDashboardRoute(navRole) : '/';
  const fullName = getFullName(displayFirstName, displayLastName);
  const initials = getUserInitials(displayFirstName, displayLastName);

  // Tablet topbar user menu (the sidebar exposes these as items instead)
  const userMenuContent = user && (
    <>
      <DropdownMenuLabel>
        <div className="flex flex-col space-y-1">
          <p className="text-sm font-medium">{fullName}</p>
          <p className="text-xs text-gray-500">{user.email}</p>
        </div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem>
        <Link to="/profile" className="flex items-center w-full">
          <UserIcon className="mr-2 h-4 w-4" />
          <span>Profile</span>
        </Link>
      </DropdownMenuItem>
      {/* Feature 075: owners manage their sub-delegates;
          hidden for SUB_DELEGATE accounts (no sub-sub-delegation) */}
      {user.role !== 'SUB_DELEGATE' && (
        <DropdownMenuItem>
          <Link to="/workspace/sub-delegates" className="flex items-center w-full">
            <Users className="mr-2 h-4 w-4" />
            <span>Manage sub-delegates</span>
          </Link>
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={logout}
        className="text-red-600 focus:text-red-600 cursor-pointer"
      >
        <LogOut className="mr-2 h-4 w-4" />
        <span>Logout</span>
      </DropdownMenuItem>
    </>
  );

  return (
    <>
      <AppSidebar
        user={isAuthenticated ? user : null}
        homeRoute={homeRoute}
        roleDisplayName={roleDisplayName}
        navItems={navItems}
        isAdmin={isAdmin}
        fullName={fullName}
        initials={initials}
        onLogout={logout}
      />

      {/* Topbar below lg; navigation lives in the MobileMenu sheet */}
      <header className="bg-[#1F4B45] border-b border-[#183B36] sticky top-0 z-50 lg:hidden">
        <div className="mx-auto w-full px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo and Role Name */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                to={homeRoute}
                className="hover:opacity-80 transition-opacity"
              >
                <div className="flex items-center gap-2">
                  <img src={bquestLogo} alt="bQuest" className="h-7 brightness-0 invert" />
                  {isAuthenticated && user && (
                    <>
                      <span className="text-sm text-white/60">|</span>
                      <span className="text-xs text-white whitespace-nowrap">{roleDisplayName}</span>
                    </>
                  )}
                </div>
              </Link>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {isAuthenticated && user ? (
                <>
                  {/* User Menu - Tablet */}
                  <div className="hidden md:block">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="flex items-center gap-2 hover:bg-white/10 px-2 text-white"
                        >
                          <Avatar className="h-8 w-8">
                            {user.profilePhoto && (
                              <AvatarImage src={user.profilePhoto} alt={fullName} />
                            )}
                            <AvatarFallback className="bg-white text-[#026857] text-sm font-bold">
                              {initials}
                            </AvatarFallback>
                          </Avatar>
                          <ChevronDown className="h-4 w-4 text-white" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        {userMenuContent}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Mobile Menu Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hover:bg-white/10 text-white"
                    onClick={() => setMobileMenuOpen(true)}
                    aria-label="Open menu"
                  >
                    <Menu className="h-6 w-6" />
                  </Button>
                </>
              ) : (
                // Unauthenticated - Show Login/Register buttons
                <div className="flex items-center gap-2">
                  <Link
                    to="/login"
                    className="flex min-h-[44px] items-center px-3 py-2 text-sm font-termina font-bold uppercase text-white/90 underline-offset-4 transition-colors hover:text-white hover:underline"
                  >
                    Login
                  </Link>
                  <Button asChild className="bg-white text-[#026857] hover:bg-white/90 font-bold">
                    <Link to="/register">Get Started</Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Sheet */}
      {isAuthenticated && user && (
        <MobileMenu
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          navItems={navItems}
          user={user}
          onLogout={logout}
        />
      )}
    </>
  );
};

